// @flow

import React, { useState } from 'react';
import { connect } from 'react-redux';
import i18n from 'app/common/lib/i18n';
import { toggleActivityItemReaction } from 'app/common/redux/actions/activityItem/updateActivityItem';
import { type State as ReduxState } from 'app/common/redux/reducers/types';
import {
  type BoardActivityItem,
  type BoardActivityType,
} from 'app/common/redux/reducers/activityItems';
import { Box, Icon, Text, Touchable } from 'gestalt';
import { compose, type Dispatch } from 'redux';
import { getNameToShow, getTimeSinceText } from 'app/common/react/components/Activities/common';

type OwnProps = {|
  parentActivity: BoardActivityItem,
  replyActivityId: ?string,
|};

type ReduxDispatchProps = {|
  toggleLike: (activityId: string, activityType: BoardActivityType, isLiked: boolean) => void,
|};

type ReduxStateProps = {|
  replyActivityItem: ?BoardActivityItem,
|};

type ConnectedProps = {|
  ...ReduxDispatchProps,
  ...ReduxStateProps,
  ...OwnProps,
|};

type Props = {|
  ...ConnectedProps,
|};

// Renders one board activity comment reply under Pin Reps in group board pin grid
export function BoardActivityPinRepReply({
  parentActivity,
  replyActivityId,
  replyActivityItem,
  toggleLike,
}: Props) {
  const {
    content: { text: replyText },
    created_at: replyCreatedAt,
    reaction_by_me: reactionByMe,
    type: replyActivityType,
    user: replyUser,
  } = replyActivityItem || {};

  const [hoverState, sethoverState] = useState(false);

  if (!replyActivityId || !replyActivityItem) {
    return null;
  }

  const handleOnTouch = ({ event }) => {
    toggleLike(replyActivityId, replyActivityType, !!reactionByMe);
  };

  const handleOnMouseEnter = ({ event }) => {
    sethoverState(true);
  };

  const handleOnMouseLeave = ({ event }) => {
    sethoverState(false);
  };

  return (
    <Box marginTop={2} display="flex" direction="row" width="100%">
      <Box
        marginRight={2}
        dangerouslySetInlineStyle={{
          __style: { borderLeftWidth: '2px', borderLeftStyle: 'solid', borderLeftColor: '#efefef' },
        }}
      />

      <Box display="flex" direction="column" width="100%">
        <Touchable
          onTouch={handleOnTouch}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        >
          {/* Username and Time */}
          <Box display="flex" direction="row">
            <Box maxWidth={124}>
              <Text size="sm" weight="bold">
                {getNameToShow(replyUser)}
              </Text>
            </Box>

            <Box marginLeft={1}>
              <Text size="sm" color="gray">
                {getTimeSinceText(replyCreatedAt)}
              </Text>
            </Box>
          </Box>

          {/* Reply */}
          <Box display="flex" direction="row">
            {/* Text */}
            <Box width="100%">
              <Box marginRight={5} width="100%">
                <Text size="sm">{replyText}</Text>
              </Box>
            </Box>

            {/* Reaction Icon */}
            <Box marginTop={-2} marginRight={1} marginLeft={2} minWidth={12}>
              {!!reactionByMe && (
                <Icon
                  icon="heart"
                  accessibilityLabel={i18n._(
                    'Liked',
                    'Accessibility lable for like sent reaction icon to comments and replies under pin rep',
                  )}
                  color="red"
                  size={12}
                />
              )}
              {!reactionByMe &&
                hoverState && (
                  <Icon
                    icon="heart-outline"
                    accessibilityLabel={i18n._(
                      'Like',
                      'Accessibility lable for send like reaction icon to comments and replies under pin rep',
                    )}
                    color="darkGray"
                    size={12}
                  />
                )}
            </Box>
          </Box>
        </Touchable>
      </Box>
    </Box>
  );
}

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  toggleLike: (activityId: string, activityType: BoardActivityType, isLiked: boolean) =>
    dispatch(
      toggleActivityItemReaction(
        activityId,
        PCONST.thrift.ReactionType.LIKE,
        activityType,
        isLiked,
      ),
    ),
});

const mapStateToProps = (state: ReduxState, props: OwnProps): ReduxStateProps => {
  const replyActivityItem = props.replyActivityId
    ? state.activityItems[props.replyActivityId]
    : null;
  return {
    replyActivityItem,
  };
};

export default compose(
  connect<ConnectedProps, OwnProps, _, _, _, _>(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(BoardActivityPinRepReply);
